import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { BusinessServices, Country, Roles } from '@shs-digi-care-monorepo/interfaces';
import { Stage } from '@shs-digi-care-monorepo/frontend-lib/enums';
import { roleNamesForStream } from '@shs-digi-care-monorepo/frontend-lib/util';
import { UserState } from '@shs-digi-care-monorepo/frontend-lib';
import { environment } from '../../../environments/environment';
import { ALL_EMEA_COUNTRIES, emeaCountriesOf } from '../utils/emea-countries.util';
import { SelectedCountryService } from './selected-country.service';

/**
 * Returns all EMEA countries except the ones provided.
 * Use in FEATURE_CONFIG to express "everyone except X".
 *
 * @example
 * countries: exclude(Country.TR)
 * // → all EMEA countries minus Turkey
 */
export function exclude(...excluded: Country[]): Country[] {
    return ALL_EMEA_COUNTRIES.filter((c) => !excluded.includes(c));
}

/**
 * Role presets
 */
/** Stream-level EMEA admin only. */
const ADMIN = [Roles.ADMIN];
/** Stream admin plus zone e-commerce stewards, who administer a set of countries. */
const ZONE_ADMIN = [Roles.ADMIN, Roles.ZONE_E_COMMERCE_STEWARD];
/** Every role with administrative permissions, down to the key country user. */
const COUNTRY_ADMIN = [Roles.ADMIN, Roles.ZONE_E_COMMERCE_STEWARD, Roles.KEY_COUNTRY_USER];
/** Roles that work on tasks. */
const TASK_OWNER_AND_ABOVE = [...COUNTRY_ADMIN, Roles.TASK_OWNER];

/**
 * All country- and role-gated features in the EMEA module.
 * Add new entries here when introducing new country- or role-specific features.
 */
export enum EmeaFeature {
    /** Instrument Delivery tile on the landing page (AT internal only) */
    INSTRUMENT_DELIVERY = 'INSTRUMENT_DELIVERY',
    /** Purchase Order tile and request type (AT, NL internal only) */
    PURCHASE_ORDER = 'PURCHASE_ORDER',
    /** Esker integration for sending requests to Esker */
    ESKER = 'ESKER',
    /** Access to the admin area as a whole */
    ADMIN_AREA = 'ADMIN_AREA',
    /** User Management */
    USER_MANAGEMENT = 'USER_MANAGEMENT',
    /** Team Management incl. escalation managers and request distribution */
    TEAM_MANAGEMENT = 'TEAM_MANAGEMENT',
    /** Request types and classifications */
    REQUEST_TYPE_MANAGEMENT = 'REQUEST_TYPE_MANAGEMENT',
    /** Translation Management */
    TRANSLATION_MANAGEMENT = 'TRANSLATION_MANAGEMENT',
    /** Admin rule set */
    RULE_SET_MANAGEMENT = 'RULE_SET_MANAGEMENT',
    /** VIP/priority customers */
    PRIORITY_MANAGEMENT = 'PRIORITY_MANAGEMENT',
    /** Maintaining one's own absences */
    OUT_OF_OFFICE = 'OUT_OF_OFFICE',
    /** Out-of-office calendar across the teams in scope */
    OUT_OF_OFFICE_CALENDAR = 'OUT_OF_OFFICE_CALENDAR',
    /** Country Switcher */
    COUNTRY_SWITCHER = 'COUNTRY_SWITCHER'
}

/**
 * Feature configuration entry defining which countries and roles can access a feature.
 */
interface FeatureConfig {
    /** Countries for which the feature is enabled. Omit to enable it for all EMEA countries. */
    countries?: Country[];
    /** Roles that have access to this feature. Empty array means all roles have access. */
    roles: Roles[];
    /** Whether the feature is limited to Siemens Healthineers internal users. */
    internalOnly?: boolean;
    /** Optional: feature is disabled on these stages */
    disabledOnStages?: Stage[];
}

/**
 * Central feature configuration map.
 * Each entry defines the countries (and optionally roles/stages) for which a feature is enabled.
 *
 * To add a new country-gated feature:
 * 1. Add an entry to the `EmeaFeature` enum
 * 2. Add the corresponding config here
 * 3. Use `EmeaFeatureService.isEnabled(EmeaFeature.YOUR_FEATURE)` in components
 */
const FEATURE_CONFIG: Record<EmeaFeature, FeatureConfig> = {
    [EmeaFeature.INSTRUMENT_DELIVERY]: {
        countries: [Country.AT],
        roles: [],
        internalOnly: true
    },
    [EmeaFeature.PURCHASE_ORDER]: {
        countries: [Country.AT, Country.NL],
        roles: [],
        internalOnly: true
    },
    [EmeaFeature.ESKER]: {
        countries: [Country.CZ, Country.PL, Country.NL, Country.AT],
        roles: []
    },
    [EmeaFeature.ADMIN_AREA]: {
        roles: COUNTRY_ADMIN
    },
    [EmeaFeature.USER_MANAGEMENT]: {
        roles: COUNTRY_ADMIN
    },
    [EmeaFeature.TEAM_MANAGEMENT]: {
        roles: COUNTRY_ADMIN
    },
    [EmeaFeature.REQUEST_TYPE_MANAGEMENT]: {
        roles: ZONE_ADMIN
    },
    [EmeaFeature.TRANSLATION_MANAGEMENT]: {
        roles: ZONE_ADMIN
    },
    [EmeaFeature.RULE_SET_MANAGEMENT]: {
        roles: ADMIN
    },
    [EmeaFeature.PRIORITY_MANAGEMENT]: {
        roles: COUNTRY_ADMIN
    },
    [EmeaFeature.OUT_OF_OFFICE]: {
        roles: [Roles.TASK_OWNER]
    },
    [EmeaFeature.OUT_OF_OFFICE_CALENDAR]: {
        roles: TASK_OWNER_AND_ABOVE
    },
    [EmeaFeature.COUNTRY_SWITCHER]: {
        roles: [Roles.TASK_OWNER, Roles.ZONE_E_COMMERCE_STEWARD]
    }
};

/**
 * Service that centralizes country-based feature gating for the EMEA module.
 *
 * Instead of scattering `isUserFromCountry(user, [...])` checks across components,
 * inject this service and check features via signals. The service reads the current
 * user from the store automatically — no need to pass user around.
 *
 * @example
 * ```ts
 * // In a component:
 * private readonly features = inject(EmeaFeatureService);
 *
 * protected canSeeEsker = this.features.isEnabled(EmeaFeature.ESKER);
 * protected canSeePurchaseOrder = this.features.isEnabled(EmeaFeature.PURCHASE_ORDER);
 * protected canSeeInstrumentDelivery = this.features.isEnabled(EmeaFeature.INSTRUMENT_DELIVERY);
 * ```
 *
 * @example
 * ```html
 * <!-- In a template: -->
 * @if (canSeeEsker()) {
 *   <button (click)="sendToEsker()">Send to Esker</button>
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class EmeaFeatureService {
    private readonly store = inject(Store);
    private readonly selectedCountry = inject(SelectedCountryService);
    private readonly me = toSignal(this.store.select(UserState.me), { initialValue: null });

    /** Role names the current user holds within the OM EMEA stream. */
    private readonly emeaRoles: Signal<Roles[]> = computed(() =>
        roleNamesForStream(this.me(), BusinessServices.OM_EMEA)
    );

    /**
     * `true` when the current user administers the OM EMEA stream. An admin of
     * another stream (e.g. OM NAM) must not be granted EMEA admin rights.
     */
    public readonly isAdmin: Signal<boolean> = computed(() => this.emeaRoles().includes(Roles.ADMIN));

    /**
     * `true` when the current user holds a country-level administrative role in the OM EMEA stream.
     */
    public readonly isCountryAdmin: Signal<boolean> = computed(() =>
        this.emeaRoles().some((role) =>
            [Roles.ADMIN, Roles.KEY_COUNTRY_USER, Roles.ZONE_E_COMMERCE_STEWARD].includes(role)
        )
    );

    /** `true` when the current user may act as a task owner in the OM EMEA stream. */
    public readonly isTaskOwner: Signal<boolean> = computed(() =>
        this.emeaRoles().some((role) =>
            [Roles.ADMIN, Roles.KEY_COUNTRY_USER, Roles.ZONE_E_COMMERCE_STEWARD, Roles.TASK_OWNER].includes(role)
        )
    );

    /** `true` when the current user is a Siemens Healthineers internal user. */
    public readonly isInternal: Signal<boolean> = computed(() => !!this.me()?.isInternal);

    /**
     * Synchronously checks whether the current user holds the given role in the
     * OM EMEA stream. Reads the user signal, so calls made inside a `computed`
     * stay reactive.
     */
    public hasRole(role: Roles): boolean {
        return this.emeaRoles().includes(role);
    }

    /**
     * Returns a computed signal that evaluates to `true` when the given feature
     * is enabled for the current user (based on their country, role, and current stage).
     */
    public isEnabled(feature: EmeaFeature): Signal<boolean> {
        return computed(() => this.evaluate(feature));
    }

    /**
     * Synchronously checks whether the given feature is enabled for the current user.
     * Reads the user signal, so calls made inside a `computed`/`effect` stay reactive.
     */
    public isActive(feature: EmeaFeature): boolean {
        return this.evaluate(feature);
    }

    /**
     * Returns a computed signal that evaluates to `true` when ALL given features
     * are enabled for the current user.
     */
    public allEnabled(...features: EmeaFeature[]): Signal<boolean> {
        return computed(() => features.every((f) => this.evaluate(f)));
    }

    /**
     * Returns a computed signal that evaluates to `true` when ANY of the given features
     * is enabled for the current user.
     */
    public anyEnabled(...features: EmeaFeature[]): Signal<boolean> {
        return computed(() => features.some((f) => this.evaluate(f)));
    }

    /**
     * Returns a computed signal of the user's assigned countries in which the
     * given feature is available, regardless of the country currently selected.
     * Empty when the user's roles or the current stage rule the feature out
     * altogether.
     */
    public countriesFor(feature: EmeaFeature): Signal<Country[]> {
        return computed(() => this.enabledCountries(feature));
    }

    /**
     * A feature is enabled when it is available in the country the user has
     * currently selected. Without a selected country — a user without country
     * assignment — any assigned country offering the feature enables it.
     */
    private evaluate(feature: EmeaFeature): boolean {
        const enabledIn = this.enabledCountries(feature);
        const selected = this.selectedCountry.selected();
        return selected ? enabledIn.includes(selected) : enabledIn.length > 0;
    }

    private enabledCountries(feature: EmeaFeature): Country[] {
        const user = this.me();
        if (!user) return [];

        const config = FEATURE_CONFIG[feature];

        // Stage restriction
        if (config.disabledOnStages?.includes(environment.stage as Stage)) {
            return [];
        }

        if (config.internalOnly && !this.isInternal()) {
            return [];
        }

        // Role restriction
        const held = this.emeaRoles();
        if (config.roles.length > 0 && !config.roles.some((role) => held.includes(role))) {
            return [];
        }

        // Country check
        const enabledIn = config.countries ?? ALL_EMEA_COUNTRIES;
        return emeaCountriesOf(user).filter((country) => enabledIn.includes(country));
    }
}
